const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')

export type ApiOptions = {
  method?: string
  body?: unknown
  token?: string | null
  idempotencyKey?: string
}

/** Resolve API-hosted assets (avatars) against VITE_API_URL when set. */
export function mediaUrl(path: string | null | undefined): string | null {
  if (!path) return null
  if (path.startsWith('data:') || path.startsWith('http://') || path.startsWith('https://')) {
    return path
  }
  if (path.startsWith('/api/')) return `${API_BASE}${path}`
  return path
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = opts.method || (opts.body !== undefined ? 'POST' : 'GET')
  const headers: Record<string, string> = {}
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey
  const hasBody = opts.body !== undefined || (method !== 'GET' && method !== 'HEAD')
  if (hasBody) headers['Content-Type'] = 'application/json'

  // Bearer-only auth — avoid cross-site cookie mode (can fail as "Failed to fetch" in strict browsers)
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      credentials: 'omit',
      body: hasBody ? JSON.stringify(opts.body ?? {}) : undefined,
    })
  } catch (e: any) {
    const base = API_BASE || '(same origin)'
    throw new Error(
      `Server offline — cannot reach ${base}. Hard-refresh (Cmd+Shift+R). Host must keep the API running.`
    )
  }
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok) {
    const err = new Error(
      String(data.message || data.error || res.statusText || 'Request failed')
    ) as Error & {
      status: number
      body: unknown
    }
    err.status = res.status
    err.body = data
    throw err
  }
  return data as T
}
